import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Base from '../Base';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo, apiRequest, teamPhoto } from '../../utils';
import Image from '../../components/images/Image';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';
import Button from '../../components/elements/Button';
import Modal from '../../components/elements/Modal';
import Toast from '../../components/forms/Toast';

const fallbackActivity = require('./../../assets/images/fallback/activity.png');

const formatTime = (v) => {
  // Pull HH:MM out of whatever the server sent (raw "14:00", ISO date,
  // ISO with Z, etc.) instead of round-tripping through new Date(). The
  // home card already does this — going through Date() here was
  // interpreting the value as UTC and shifting it into the browser's
  // local timezone, so the displayed time drifted by the local offset
  // (e.g. +4 hours in UTC+4).
  if (!v) return '';
  const m = String(v).match(/(\d{1,2}):(\d{2})/);
  if (!m) return v;
  return `${m[1].padStart(2, '0')}:${m[2]}`;
};

const StarRating = ({ value, onChange, max = 5 }) => {
  const stars = [];
  for (let i = 0; i < max; i++) {
    stars.push(
      <span key={i} onClick={() => onChange(i + 1)} style={{ cursor: 'pointer' }}>
        <Icon
          name="star"
          theme={i < value ? 'primary' : 'disabled'}
          pack="sr"
          className="mx-1"
          style={{ fontSize: '1.8rem', transition: 'transform 0.1s ease-in-out' }}
        />
      </span>
    );
  }
  return <div className="d-flex justify-content-center mb-2">{stars}</div>;
};

const RankBadge = ({ rank }) => {
  const cls =
    rank === 1
      ? 'rank-badge rank-badge--gold'
      : rank === 2
      ? 'rank-badge rank-badge--silver'
      : rank === 3
      ? 'rank-badge rank-badge--bronze'
      : 'rank-badge';
  return <span className={cls}>{rank}</span>;
};

const Page = () => {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [crewRating, setCrewRating] = useState(0);
  const [challengeRating, setChallengeRating] = useState(0);

  useEffect(() => {
    fetchUserInfo('/score', setUserInfo, setLoading, setError, ['selectedScoreId']);
  }, []);

  const getUserScoreId = () => {
    const userTeam = userInfo?.teams?.find((team) => team.team_id === userInfo.user_team_id);
    return userTeam?.id;
  };

  const submitRating = async () => {
    try {
      const res = await apiRequest('POST', '/rate-crew-challenge', {
        score_id: getUserScoreId(),
        crew_rating: crewRating,
        challenge_rating: challengeRating,
      });

      setError(null);
      setSuccessMessage(res.message);
      setShowRatingModal(false);
      setShowToast(true);

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      setError(error.message);
      setSuccessMessage(null);
      setShowToast(true);
    }
  };

  const activity = userInfo?.teams?.[0];

  const activityPhoto = activity?.activity_photo || fallbackActivity;
  const userScoreId = getUserScoreId();
  const teams = userInfo?.teams || [];

  return (
    <Base activeNav="home">
      <div className="score-page">
        <Back to="/scores" />

        {/* HERO — banner-style header with image as backdrop */}
        <div className="score-hero2">
          <div
            className="score-hero2__banner"
            style={{ backgroundImage: `url("${activityPhoto}")` }}
          />
          <div className="score-hero2__overlay" />
          <div className="score-hero2__content">
            <Image
              url={activityPhoto}
              className="score-hero2__avatar"
            />
            <h2 className="score-hero2__title">{activity?.activity_name}</h2>
            {(activity?.start_time || activity?.end_time) && (
              <div className="score-hero2__time">
                <Icon name="clock" pack="sr" theme="light" />
                <span>
                  {formatTime(activity?.start_time)}
                  {activity?.end_time && ` – ${formatTime(activity.end_time)}`}
                </span>
              </div>
            )}
            {userScoreId && (
              <Button
                onClick={() => {
                  const score = userInfo?.teams?.find((team) => team.team_id === userInfo.user_team_id);
                  setCrewRating(score?.crew_rating || 0);
                  setChallengeRating(score?.challenge_rating || 0);
                  setShowRatingModal(true);
                }}
                className="score-hero2__cta"
                styles={{}}
              >
                <Icon name="star" pack="sr" className="me-2" theme="light" />
                {t('rate')}
              </Button>
            )}
          </div>
        </div>

        <Modal
          show={showRatingModal}
          title={t('rateCrewChallenge')}
          onClose={() => setShowRatingModal(false)}
          onSubmit={submitRating}
        >
          <div className="text-center mb-4">
            <div style={{ fontSize: '1.1rem', textDecoration: 'underline', fontWeight: 500 }}>
              {t('rateTheCrew')}
            </div>
            <StarRating value={crewRating} onChange={setCrewRating} />
          </div>
          <div className="text-center mb-2">
            <div style={{ fontSize: '1.1rem', textDecoration: 'underline', fontWeight: 500 }}>
              {t('rateTheChallenge')}
            </div>
            <StarRating value={challengeRating} onChange={setChallengeRating} />
          </div>
        </Modal>

        <Toast showToast={showToast} setShowToast={setShowToast} message={t(successMessage || error)} />

        {/* STANDINGS */}
        {teams.length > 0 && (
          <div className="score-standings">
            <h3 className="score-standings__title">{t('leaderboard')}</h3>
            <div className="score-standings__list">
              {teams.map((team) => {
                const isOwn = team.team_id === userInfo.user_team_id;
                const isPodium = team.rank >= 1 && team.rank <= 3;
                const hasJoker = team.joker_id && team.joker_id === team.activity_id;
                const hasLadies = team.is_ladies === 1;
                const hasDq = team.is_disqualified === 1;
                const jokerPoints = team.joker_points || 0;
                const ladiesPoints = team.ladies_points || 0;
                return (
                  <Link
                    key={team.id}
                    to="/team"
                    onClick={() => sessionStorage.setItem('otherTeamId', team.team_id)}
                    className={`score-row ${isOwn ? 'score-row--own' : ''} ${isPodium ? 'score-row--podium' : ''}`}
                  >
                    <RankBadge rank={team.rank} />

                    <Image
                      size="2.8rem"
                      className="score-row__avatar circle"
                      url={teamPhoto(team.team_photo)}
                    />

                    <div className="score-row__body">
                      <div className="score-row__name">
                        {team.is_rival && (
                          <Icon name="megaphone" className="me-1" theme="primary" pack="sr" />
                        )}
                        <strong>{team.name}</strong>
                        {isOwn && <span className="score-row__you-pill">{t('you', { defaultValue: 'You' })}</span>}
                      </div>
                      <div className="score-row__meta">
                        {team.house_color && (
                          <span
                            className="score-row__house-dot"
                            style={{ backgroundColor: team.house_color }}
                          />
                        )}
                        <span className="score-row__company">{team.company_name}</span>
                        {(hasJoker || hasLadies || hasDq) && (
                          <span className="score-row__chips">
                            {hasJoker && (
                              <span className="score-row__chip score-row__chip--joker" title={t('joker')}>
                                <Icon name="star" theme="primary" />
                              </span>
                            )}
                            {hasLadies && (
                              <span className="score-row__chip score-row__chip--ladies" title={t('ladies')}>
                                <Icon name="woman-head" />
                              </span>
                            )}
                            {hasDq && (
                              <span className="score-row__chip score-row__chip--dq" title={t('disqualified')}>
                                <Icon name="user-xmark" />
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                      {isOwn && !hasDq && (
                        <div className="score-breakdown">
                          <span className="score-breakdown__item score-breakdown__item--place">
                            <span className="score-breakdown__label">{t('place')}</span>
                            <span className="score-breakdown__value">+{team.place_points || 0}</span>
                          </span>
                          {jokerPoints !== 0 && (
                            <span className="score-breakdown__item score-breakdown__item--joker">
                              <Icon name="star" theme="primary" />
                              <span className="score-breakdown__label">{t('joker')}</span>
                              <span className="score-breakdown__value">+{jokerPoints}</span>
                            </span>
                          )}
                          {ladiesPoints !== 0 && (
                            <span className="score-breakdown__item score-breakdown__item--ladies">
                              <Icon name="woman-head" />
                              <span className="score-breakdown__label">{t('ladies')}</span>
                              <span className="score-breakdown__value">+{ladiesPoints}</span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="score-row__points">
                      <span className="score-row__points-value">+{team.points}</span>
                      <span className="score-row__points-label">{t('pts')}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Base>
  );
};

export default Page;
